import { lazy } from "react";
import {
  createBrowserRouter,
  Navigate,
  type RouteObject,
} from "react-router-dom";
import { Callback } from "../lib/auth/Callback";
import { SilentRenew } from "../lib/auth/SilentRenew";
import { ProtectedRoute } from "../lib/auth/ProtectedRoute";
import { RoleRoute } from "../lib/auth/RoleRoute";
import { HomeRedirect } from "../lib/auth/HomeRedirect";
import { App } from "../App";
import { LazyRoute } from "../components/LazyRoute";
import HomeScreen from "../screens/HomeScreen";
import { TimesheetScreen } from "../screens/TimesheetScreen";
import { ProfilScreen } from "../screens/ProfilScreen";
import TerrainScreen from "../screens/TerrainScreen";
import TasksScreen from "../screens/TasksScreen";
import ProjectsScreen from "../screens/ProjectsScreen";
import ProjectDetailScreen from "../screens/ProjectDetailScreen";
import DigestScreen from "../screens/DigestScreen";
import HierScreen from "../screens/HierScreen";
import MoreScreen from "../screens/MoreScreen";

// Écrans manager/admin chargés à la demande : hors du bundle du worker.
const AnomaliesScreen = lazy(() =>
  import("../screens/AnomaliesScreen").then((m) => ({
    default: m.AnomaliesScreen,
  })),
);
const ReviewScreen = lazy(() =>
  import("../screens/ReviewScreen").then((m) => ({ default: m.ReviewScreen })),
);
const AdminScreen = lazy(() =>
  import("../screens/AdminScreen").then((m) => ({ default: m.AdminScreen })),
);
const TeamScreen = lazy(() => import("../screens/TeamScreen"));
const BillingScreen = lazy(() => import("../screens/BillingScreen"));
const ChantiersScreen = lazy(() => import("../screens/ChantiersScreen"));
const ImputationRulesScreen = lazy(
  () => import("../screens/ImputationRulesScreen"),
);

// Exportée à part : les tests montent la vraie table dans un routeur mémoire.
export const routes: RouteObject[] = [
  { path: "/auth/callback", element: <Callback /> },
  { path: "/auth/silent", element: <SilentRenew /> },
  {
    element: (
      <ProtectedRoute>
        <App />
      </ProtectedRoute>
    ),
    children: [
      {
        path: "/recap",
        element: (
          <RoleRoute roles={["manager", "admin"]}>
            <DigestScreen />
          </RoleRoute>
        ),
      },
      { path: "/hier", element: <HierScreen /> },
      { path: "/plus", element: <MoreScreen /> },
      { path: "/badger", element: <HomeScreen /> },
      { path: "/activites", element: <Navigate to="/badger" replace /> },
      { path: "/timesheet", element: <TimesheetScreen /> },
      { path: "/profil", element: <ProfilScreen /> },
      {
        path: "/anomalies",
        element: (
          <LazyRoute>
            <AnomaliesScreen />
          </LazyRoute>
        ),
      },
      { path: "/terrain", element: <TerrainScreen /> },
      { path: "/taches", element: <TasksScreen /> },
      { path: "/projets", element: <ProjectsScreen /> },
      { path: "/projets/:id", element: <ProjectDetailScreen /> },
      {
        path: "/equipe",
        element: (
          <RoleRoute roles={["manager", "admin"]}>
            <LazyRoute>
              <TeamScreen />
            </LazyRoute>
          </RoleRoute>
        ),
      },
      {
        path: "/manager",
        element: <Navigate to="/equipe?tab=temps" replace />,
      },
      {
        path: "/revue",
        element: (
          <RoleRoute roles={["manager", "admin"]}>
            <LazyRoute>
              <ReviewScreen />
            </LazyRoute>
          </RoleRoute>
        ),
      },
      {
        path: "/facturation",
        element: (
          <RoleRoute roles={["manager", "admin"]}>
            <LazyRoute>
              <BillingScreen />
            </LazyRoute>
          </RoleRoute>
        ),
      },
      {
        path: "/chantiers",
        element: (
          <LazyRoute>
            <ChantiersScreen />
          </LazyRoute>
        ),
      },
      {
        // Ouvert au salarié pour ses seules règles : TAWA l'y autorise
        // (timesheet:self_edit, canManageRule sur soi).
        path: "/regles-imputation",
        element: (
          <LazyRoute>
            <ImputationRulesScreen />
          </LazyRoute>
        ),
      },
      {
        path: "/admin",
        element: (
          <RoleRoute roles={["admin"]}>
            <LazyRoute>
              <AdminScreen />
            </LazyRoute>
          </RoleRoute>
        ),
      },
    ],
  },
  // `/` et les chemins inconnus passent par la connexion (returnUrl = le chemin
  // demandé, `/` au retour de Keycloak), puis vers l'accueil. Hors du shell :
  // rien ne s'affiche avant la redirection.
  {
    path: "/",
    element: (
      <ProtectedRoute>
        <HomeRedirect />
      </ProtectedRoute>
    ),
  },
  {
    path: "*",
    element: (
      <ProtectedRoute>
        <HomeRedirect />
      </ProtectedRoute>
    ),
  },
];

export const router = createBrowserRouter(routes);
